/**
 * Edit one destination's link into a routing group.
 *
 * **Link-scoped, and the form says so.** Name, endpoint, delivery method and
 * timeout live on the shared `destinations` row, which several groups may point
 * at — on the live system Endpoint One, Two and Three are each linked into
 * three different groups. That is the model working as designed (plan D2:
 * destinations carry defaults, links override them), but it means editing a
 * shared field from a screen headed "this group" would quietly change routing
 * for two other groups. So those fields are shown read-only here, with where to
 * change them, rather than being silently absent.
 *
 * **Save writes only what changed.** Filters, suppression lists and days a link
 * has none of are inherited from the destination, and only NULL inherits: this
 * form used to send every field back, so editing a price turned an inherited
 * Monday to Friday into `[]`, which is every day, dropped the destination's
 * filters and suppression lists the same way, and switched a paused destination
 * back on. Anything the operator did not touch now stays exactly as stored.
 */
import { useEffect, useState } from 'react';
import {
  Alert, Anchor, Button, Divider, Grid, Group, Modal, MultiSelect, NumberInput, Stack,
  Switch, Text, TextInput,
} from '@mantine/core';
import type { components } from '../api/schema';
import { notifications } from '@mantine/notifications';
import { Link } from 'react-router-dom';
import {
  useCampaignDetail, useCampaigns, useDestinations, useUnlinkDestination,
  useUpdateGroupDestination,
} from '../api/queries';
import {
  SmartFilters, asFilters, toStored, unfinishedListsReason, type SmartFilter,
} from './SmartFilterEditor';
import { useVocab } from '../vocab';
import { DAY_OPTIONS, daysPlaceholder, daysWords, isEveryDay, sameDays, sortedDays } from './ActiveDays';

type CampaignDetail = NonNullable<ReturnType<typeof useCampaignDetail>['data']>;
type RouteGroup = CampaignDetail['groups'][number];
type GroupDestination = RouteGroup['destinations'][number];

/** A cap as words: 0 or less is no cap. */
function capWords(cap: number): string {
  return cap > 0 ? cap.toLocaleString('en-GB') : 'no limit';
}

/** Two id lists hold the same ids, in any order. */
function sameIds(a: string[], b: string[]): boolean {
  return JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());
}

export function EditDestinationModal({
  campaignId,
  group,
  destination,
  onClose,
}: {
  campaignId: string | undefined;
  group: RouteGroup | null;
  destination: GroupDestination | null;
  onClose: () => void;
}) {
  const v = useVocab();
  const save = useUpdateGroupDestination(campaignId);
  const unlink = useUnlinkDestination(campaignId);
  const { data: allDestinations } = useDestinations();
  // Any campaign can be a suppression source — there is no separate kind, and
  // the live system suppresses against "ESB Suppression", an ordinary
  // campaign. So the list is not filtered.
  const { data: allCampaigns } = useCampaigns();
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  // How many groups use this destination decides what "remove" actually does.
  // On its last link the engine deletes the destination row outright, so the
  // operator has to be told which of the two they are about to do.
  const linkCount =
    allDestinations?.destinations.find((d) => d.id === destination?.destination_id)?.link_count ?? 0;
  const isLastLink = linkCount <= 1;

  const [price, setPrice] = useState<number>(0);
  const [priority, setPriority] = useState<number>(1);
  const [weight, setWeight] = useState<number>(1);
  const [active, setActive] = useState(true);
  const [daily, setDaily] = useState<number>(0);
  const [weekly, setWeekly] = useState<number>(0);
  const [monthly, setMonthly] = useState<number>(0);
  const [ownCaps, setOwnCaps] = useState(false);
  // A link either has its own days or uses the destination's. Only leaving the
  // second state for the first writes anything.
  const [ownDays, setOwnDays] = useState(false);
  const [days, setDays] = useState<string[]>([]);
  const [andFilters, setAndFilters] = useState<SmartFilter[]>([]);
  const [orFilters, setOrFilters] = useState<SmartFilter[]>([]);
  const [suppression, setSuppression] = useState<string[]>([]);
  const [phoneCheck, setPhoneCheck] = useState(false);
  const [acceptAbsent, setAcceptAbsent] = useState(true);

  useEffect(() => {
    if (!destination) return;
    // The link's own override, not the resolved price: showing the resolved
    // value here and saving it back would silently promote a destination
    // default (or the buyer's legacy blob) into a hard link override.
    setPrice(destination.price.link_value ?? 0);
    setPriority(destination.priority);
    setWeight(destination.weight);
    setActive(destination.active);
    setDaily(destination.caps.daily_cap);
    setWeekly(destination.caps.weekly_cap);
    setMonthly(destination.caps.monthly_cap);
    setOwnCaps(destination.own_caps);
    setOwnDays(destination.days?.source === 'link');
    setDays(destination.days?.source === 'link' ? destination.days.value.map(String) : []);
    setAndFilters(asFilters(destination.smart_filters, 'and'));
    setOrFilters(asFilters(destination.smart_filters, 'or'));
    setSuppression(destination.suppression_campaign_ids ?? []);
    setPhoneCheck(destination.phone_check?.enabled ?? false);
    setAcceptAbsent(destination.phone_check?.accept_absent ?? true);
  }, [destination]);

  const cannotSave = unfinishedListsReason([...andFilters, ...orFilters]);

  async function submit() {
    if (!destination || !group) return;
    // Only what changed: sending a value the form merely read would turn a
    // destination-wide default into a setting on this link alone.
    const storedFilters = toStored(
      asFilters(destination.smart_filters, 'and'),
      asFilters(destination.smart_filters, 'or'),
    );
    const filters = toStored(andFilters, orFilters);
    const hadOwnDays = destination.days?.source === 'link';
    const chosenDays = sortedDays(days.map(Number));
    const body: components['schemas']['UpdateGroupDestinationBody'] = {
      ...(price !== (destination.price.link_value ?? 0) ? { buying_price: price } : {}),
      ...(priority !== destination.priority ? { priority } : {}),
      ...(weight !== destination.weight ? { weight } : {}),
      ...(active !== destination.active ? { active } : {}),
      ...(daily !== destination.caps.daily_cap ? { daily_cap: daily } : {}),
      ...(weekly !== destination.caps.weekly_cap ? { weekly_cap: weekly } : {}),
      ...(monthly !== destination.caps.monthly_cap ? { monthly_cap: monthly } : {}),
      ...(ownCaps !== destination.own_caps ? { own_caps: ownCaps } : {}),
      ...(hadOwnDays && !ownDays ? { inherit_active_days: true } : {}),
      ...(ownDays && (!hadOwnDays || !sameDays(chosenDays, destination.days?.value ?? []))
        ? { active_days: chosenDays }
        : {}),
      ...(JSON.stringify(filters) !== JSON.stringify(storedFilters) ? { smart_filters: filters } : {}),
      ...(!sameIds(suppression, destination.suppression_campaign_ids ?? [])
        ? { suppression_campaign_ids: suppression }
        : {}),
      ...(phoneCheck !== (destination.phone_check?.enabled ?? false)
        ? { phone_check: phoneCheck }
        : {}),
      ...(acceptAbsent !== (destination.phone_check?.accept_absent ?? true)
        ? { phone_check_accept_absent: acceptAbsent }
        : {}),
    };
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    try {
      await save.mutateAsync({
        groupId: group.id,
        destinationId: destination.destination_id,
        body,
      });
      notifications.show({
        color: 'teal',
        title: 'Link updated',
        message: `${destination.name} in ${group.name} saved.`,
      });
      onClose();
    } catch (e) {
      notifications.show({ color: 'red', title: 'Save failed', message: (e as Error).message });
    }
  }

  async function doRemove() {
    if (!destination || !group) return;
    try {
      const r = await unlink.mutateAsync({
        groupId: group.id,
        destinationId: destination.destination_id,
      });
      notifications.show({
        color: r.destination_deleted ? 'orange' : 'teal',
        title: r.destination_deleted ? `${v.One('destination')} deleted` : `Removed from ${v.one('routeGroup')}`,
        message: r.destination_deleted
          ? `${destination.name} was in no other ${v.one('routeGroup')}, so it has been deleted entirely.`
          : `${destination.name} no longer receives ${v.many('lead')} from ${group.name}.`,
      });
      setConfirmingRemove(false);
      onClose();
    } catch (e) {
      notifications.show({ color: 'red', title: 'Remove failed', message: (e as Error).message });
    }
  }

  const resolvedElsewhere =
    destination && price === 0 && destination.price.source !== 'link';
  // Optional for the minutes the web app is live ahead of the server.
  const destinationDays = destination?.days?.destination_value ?? [];
  const destinationDaysWords = daysWords(destinationDays);
  const chosenWords = daysWords(days.map(Number));
  const inheritedLists = (destination?.inherited_suppression_campaign_ids ?? []).map(
    (id) => allCampaigns?.campaigns.find((c) => c.id === id)?.name ?? id,
  );

  return (
    <Modal
      opened={destination !== null}
      onClose={onClose}
      size="xl"
      title={destination ? `${destination.name} in ${group?.name ?? ''}` : 'Edit link'}
    >
      {destination && group && (
        <Stack>
          <Alert color="gray" variant="light" title={`Shared across ${v.many('routeGroup')}`}>
            <Text size="sm">
              {v.p.url} <Text span ff="monospace">{destination.endpoint_url || 'not set'}</Text>,
              method <Text span fw={600}>{destination.delivery_method || destination.method}</Text> and
              timeout <Text span fw={600}>{destination.timeout_ms}ms</Text>
              {` belong to the ${v.one('destination')} itself, which other ${v.many('routeGroup')} may also use. They are not editable here. Everything below applies to this link only.`}
            </Text>
          </Alert>

          <Grid gutter="sm">
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <NumberInput
                label="Price override (£)"
                description={`0 uses the ${v.one('destination')}'s default`}
                min={0}
                step={0.5}
                decimalScale={2}
                value={price}
                onChange={(v) => setPrice(Number(v) || 0)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <NumberInput
                label="Priority"
                description="Trial order in priority mode"
                min={0}
                value={priority}
                onChange={(v) => setPriority(Number(v) || 0)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <NumberInput
                label="Weight"
                description="Share in weighted mode"
                min={0}
                value={weight}
                onChange={(v) => setWeight(Number(v) || 0)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <TextInput
                label={v.One('buyer')}
                description={
                  // A rep has no buyer to change.
                  destination.delivery_method === 'assign' ? undefined : (
                    <Anchor component={Link} to={`/destinations/${destination.destination_id}`} size="xs">
                      {`Change on the ${v.one('destination')} page`}
                    </Anchor>
                  )
                }
                value={destination.buyer_name || destination.buyer_id}
                readOnly
              />
            </Grid.Col>
          </Grid>

          {resolvedElsewhere && (
            <Text size="xs" c="dimmed">
              No override set, so this link currently prices from{' '}
              {destination.price.source === 'destination'
                ? `the ${v.one('destination')}'s default`
                : `the ${v.one('buyer')}'s settings blob`}
              . Leaving it at 0 keeps that behaviour.
            </Text>
          )}

          {/*
            A cap of 0 on a link uses the destination's own cap for that
            period (unlimited if it has none). A link can opt out with "own
            caps", where 0 means unlimited whatever the destination says.
          */}
          <Divider
            label={ownCaps ? 'Caps (0 means unlimited)' : `Caps (0 uses the ${v.one('destination')}'s cap)`}
            labelPosition="left"
          />
          <Switch
            label={`Ignore the ${v.one('destination')}'s caps`}
            description="This link's caps are its own, so 0 means unlimited"
            checked={ownCaps}
            onChange={(e) => setOwnCaps(e.currentTarget.checked)}
          />
          <Grid gutter="sm">
            <Grid.Col span={4}>
              <NumberInput label="Daily" min={0} value={daily} onChange={(v) => setDaily(Number(v) || 0)} />
            </Grid.Col>
            <Grid.Col span={4}>
              <NumberInput label="Weekly" min={0} value={weekly} onChange={(v) => setWeekly(Number(v) || 0)} />
            </Grid.Col>
            <Grid.Col span={4}>
              <NumberInput label="Monthly" min={0} value={monthly} onChange={(v) => setMonthly(Number(v) || 0)} />
            </Grid.Col>
          </Grid>
          <Text size="xs" c="dimmed">
            Currently used: {destination.caps.daily_count} today, {destination.caps.weekly_count} this
            week, {destination.caps.monthly_count} this month.
          </Text>
          <Text size="xs" c="dimmed">
            {`Enforced now: ${capWords(destination.effective_caps.daily_cap)} a day, ${capWords(destination.effective_caps.weekly_cap)} a week, ${capWords(destination.effective_caps.monthly_cap)} a month.`}
          </Text>

          <Divider label="Active days" labelPosition="left" />
          {ownDays ? (
            <MultiSelect
              aria-label="Active days"
              data={DAY_OPTIONS}
              value={days}
              onChange={setDays}
              placeholder={daysPlaceholder(days)}
              description={
                isEveryDay(days.map(Number)) && !isEveryDay(destinationDays)
                  ? `Every day on this ${v.one('route')}, even though the ${v.one('destination')} is ${destinationDaysWords}. Evaluated in UK time.`
                  : `${chosenWords}, set on this ${v.one('route')}. Evaluated in UK time.`
              }
              clearable
            />
          ) : (
            <MultiSelect
              aria-label="Active days"
              data={DAY_OPTIONS}
              value={destinationDays.map(String)}
              placeholder={daysPlaceholder(destinationDays)}
              description={
                isEveryDay(destinationDays)
                  ? `Every day. The ${v.one('destination')} sets no days. Evaluated in UK time.`
                  : `${destinationDaysWords}, from the ${v.one('destination')}. Evaluated in UK time.`
              }
              disabled
            />
          )}
          <Group gap="sm">
            {ownDays ? (
              <Button
                size="compact-xs"
                variant="default"
                onClick={() => {
                  setOwnDays(false);
                  setDays([]);
                }}
              >
                {`Use the ${v.one('destination')}'s days (${destinationDaysWords})`}
              </Button>
            ) : (
              <Button
                size="compact-xs"
                variant="default"
                onClick={() => {
                  setOwnDays(true);
                  setDays(destinationDays.map(String));
                }}
              >
                {`Set days for this ${v.one('route')}`}
              </Button>
            )}
            {destination.delivery_method !== 'assign' && (
              <Anchor component={Link} to={`/destinations/${destination.destination_id}`} size="xs">
                {`Change on the ${v.one('destination')} page`}
              </Anchor>
            )}
          </Group>

          <Divider label="Suppression" labelPosition="left" />
          <MultiSelect
            /* A hashed list says so in the option. Choosing one changes what
               the engine compares — the lead is hashed the same way before
               the lookup — and that is worth seeing when picking, not only
               after nothing ever matches. */
            data={(allCampaigns?.campaigns ?? []).map((c) => ({
              value: c.id,
              label: c.hash_format && c.hash_format !== 'plain'
                ? `${c.name} (${c.hash_format.toUpperCase()})`
                : c.name,
            }))}
            value={suppression}
            onChange={setSuppression}
            placeholder={inheritedLists.length > 0 ? `Using the ${v.one('destination')}'s` : 'Nothing suppressed'}
            label={`Do not send ${v.a('lead')} already in these ${v.many('campaign')}`}
            description={`Matched on email or phone before routing starts, so a suppressed ${v.one('lead')} never reaches this ${v.one('destination')}. Any ${v.one('campaign')} can be used as a list; one marked MD5 or SHA256 arrived hashed and is matched on the hash.`}
            searchable
            clearable
          />
          {inheritedLists.length > 0 && suppression.length === 0 && (
            <Text size="xs" c="dimmed">
              {`None of its own, so the ${v.one('destination')}'s apply: ${inheritedLists.join(', ')}. Choosing any here replaces them on this ${v.one('route')}.`}
            </Text>
          )}

          <Divider label="Link filters" labelPosition="left" />
          <Text size="xs" c="dimmed">
            {`These run after the ${v.one('routeGroup')}'s own filters. ${v.A('lead')} has to pass both.`}
          </Text>
          {destination.inherited_filter_count > 0 && andFilters.length + orFilters.length === 0 && (
            <Text size="xs" c="dimmed">
              {`None of its own, so the ${v.one('destination')}'s ${destination.inherited_filter_count === 1 ? 'filter applies' : `${destination.inherited_filter_count} filters apply`}. Adding any here replaces ${destination.inherited_filter_count === 1 ? 'it' : 'them'} on this ${v.one('route')}.`}
            </Text>
          )}
          <SmartFilters
            andFilters={andFilters}
            orFilters={orFilters}
            enabled
            setAndFilters={setAndFilters}
            setOrFilters={setOrFilters}
            campaignIds={campaignId ? [campaignId] : undefined}
            previewCampaignId={campaignId}
          />

          <Divider label="Phone check" labelPosition="left" />
          <Switch
            label="Check phone numbers before sending"
            description={`Each ${v.one('lead')}'s number is checked with the mobile networks (HLR). Dead, unassigned and badly formatted numbers are not sent. Each check is charged.`}
            checked={phoneCheck}
            onChange={(e) => setPhoneCheck(e.currentTarget.checked)}
          />
          {phoneCheck && (
            <Switch
              label="Still send numbers that are switched off"
              description="A real number whose phone is off right now. Off means only live numbers are sent."
              checked={acceptAbsent}
              onChange={(e) => setAcceptAbsent(e.currentTarget.checked)}
            />
          )}

          <Divider />
          <Switch
            label="Link is active"
            description={`Off means this ${v.one('routeGroup')} never tries this ${v.one('destination')}`}
            checked={active}
            onChange={(e) => setActive(e.currentTarget.checked)}
          />

          <Group justify="space-between">
            <Button variant="subtle" color="red" onClick={() => setConfirmingRemove(true)}>
              {`Remove from ${v.one('routeGroup')}`}
            </Button>
            <Group>
              {cannotSave && <Text size="xs" c="dimmed" data-testid="save-blocked">{cannotSave}</Text>}
              <Button variant="subtle" onClick={onClose}>Cancel</Button>
              <Button loading={save.isPending} disabled={!!cannotSave} onClick={submit}>Save link</Button>
            </Group>
          </Group>

          <Modal
            opened={confirmingRemove}
            onClose={() => setConfirmingRemove(false)}
            title={isLastLink ? `Delete ${destination.name}?` : `Remove from ${group.name}?`}
            zIndex={1002}
          >
            <Stack>
              {isLastLink ? (
                <Alert color="red" variant="light" title={`This deletes the ${v.one('destination')} itself`}>
                  <Text size="sm">
                    {`${destination.name} is not used by any other ${v.one('routeGroup')}, so removing it here deletes the ${v.one('destination')} and its filters outright. Its ${v.p.urlLower} configuration is gone and cannot be recovered from this app. You would have to set it up again from scratch.`}
                  </Text>
                </Alert>
              ) : (
                <Alert color="gray" variant="light" title={`The ${v.one('destination')} survives`}>
                  <Text size="sm">
                    {`${destination.name} is used by ${linkCount} groups. This only unlinks it from ${group.name}; the ${v.one('destination')} and its other links are untouched, and you can add it back later.`}
                  </Text>
                </Alert>
              )}
              <Group justify="flex-end">
                <Button variant="subtle" onClick={() => setConfirmingRemove(false)}>Cancel</Button>
                <Button color="red" loading={unlink.isPending} onClick={doRemove}>
                  {isLastLink ? `Delete ${v.one('destination')}` : `Remove from ${v.one('routeGroup')}`}
                </Button>
              </Group>
            </Stack>
          </Modal>
        </Stack>
      )}
    </Modal>
  );
}
